import ErrorDialog from "components/Elements/ErrorDialog";
import {
	Route,
	Navigate,
	createBrowserRouter,
	createRoutesFromElements,
	RouterProvider,
} from "react-router-dom";
import access from "helpers/access";
import MainApp from "pages";
import Launch from "pages/Launch/Launch";
import Login from "pages/Login/Login";
import ProtectedRoute from "components/HOC/ProtectedRoute";
import ForgotPassword from "pages/ForgotPassword/ForgotPassword";
import ResetPassword from "pages/ResetPassword/ResetPassword";
import RegisterUserEmail from "pages/RegisterUser/RegisterUser";
import Test from "pages/Test";
import {
    appPath,
    applicationPortalPath,
    defectsPath,
    feedbackPath,
    releaseNotesPath,
    releaseNotesAdminPath,
    maintenanceBannerAdminPath,
    loginPath,
    modelsPath,
    noticeboardPath,
    modelImport,
    usersPath,
    clientsPath,
    sitePath,
    applicationListPath,
    siteAppPath,
    servicesPath,
    clientPath,
    newAnalyticsPath,
    aiConfigurationPath,
} from "helpers/routePaths";
import "./App.scss";
import Portal from "pages/ApplicationPortal/Portal";
import ServicesPage from "pages/Services/Links/ServicesPage";
import ModelsPage from "pages/Models/Links/ModelsPage";
import DefectsPage from "pages/Defects/Links/DefectsPage";
import FeedbackPage from "pages/Feedback/Links/FeedbackPage";
import NoticeBoardsPage from "pages/Noticeboards/Links/NoticeBoards";
import ReleaseNotesPage from "components/ProductUpdates/ReleaseNotesPage";
import ReleaseNotesAdminPage from "pages/ReleaseNotesAdmin";
import MaintenanceBannerAdminPage from "pages/MaintenanceBannerAdmin";
import AccessRoute from "components/HOC/AccessRoute";
import ModelMapData from "pages/Models/ModelMapData";
import UsersPage from "pages/Users/Links/UsersPage";
import { userRoutes } from "pages/Users/Links/UserPage";
import SingleComponent from "pages/Users/Links/SingleComponent";
import SitePage from "pages/Clients/Sites/Links/SitePage";
import ApplicationPage from "pages/Applications/Links/ApplicationPage";
import SiteAppPage from "pages/Clients/Sites/SiteApplication/Links/SiteAppPage";
import ClientPage from "pages/Clients/Links/ClientPage";
import RoleRoute from "components/HOC/RoleRoute";
import roles from "helpers/roles";
import ClientDetails from "pages/Clients/ClientDetailScreen/ClientDetails";
import ClientTerminology from "pages/Clients/ClientDetailScreen/ClientTerminology";
import { customCaptionsPath } from "helpers/routePaths";
import ProtectedLogin from "components/HOC/ProtectedLogin";
import SuccessDialog from "components/Elements/SuccessDialog";
import ExperimentalAnalytics from "pages/Analytics";
import AIConfigurationPage from "pages/AIConfiguration";
import { FeatureFlagsProvider } from "contexts/FeatureFlagsContext";
import { withSiteAppSwitching } from "components/HOC/WithSiteAppSwitching";

// Create wrapped components
const WrappedServicesPage = withSiteAppSwitching(ServicesPage);
const WrappedDefectsPage = withSiteAppSwitching(DefectsPage);
const WrappedFeedbackPage = withSiteAppSwitching(FeedbackPage);
const WrappedModelsPage = withSiteAppSwitching(ModelsPage);
// Report-email "manage subscription" links land on
// /app/me?siteAppId=...&subscriptionId=...; the HOC prompts a tenant switch on
// siteAppId mismatch so the deep-linked subscription resolves on the right
// tenant before the profile card auto-opens it.
const WrappedSingleComponent = withSiteAppSwitching(SingleComponent);

const router = createBrowserRouter(
	createRoutesFromElements(
		<>
			<Route path="/" element={<Navigate to={loginPath} />} />
			<Route
				path={loginPath}
				element={
					<ProtectedLogin>
						<Login />
					</ProtectedLogin>
				}
			/>
			<Route path="/forgot-password" element={<ForgotPassword />} />
			<Route path="/ResetPassword" element={<ResetPassword />} />
			<Route path="/register" element={<RegisterUserEmail />} />
			<Route path="/launch" element={<Launch />} />

			{/* Protected Routes */}
			<Route
				path={appPath}
				element={
					<ProtectedRoute>
						<FeatureFlagsProvider>
							<MainApp />
						</FeatureFlagsProvider>
					</ProtectedRoute>
				}
			>
				{/* PORTAL PAGE */}
				<Route path={applicationPortalPath} element={<Portal />} />
				{/* CLIENTS PAGE */}
				<Route path={`${clientsPath}/*`} element={<ClientPage />} />
				<Route element={<RoleRoute roles={[roles.clientAdmin]} />}>
					<Route path={`${clientPath}/:id`} element={<ClientDetails />} />
					<Route
						path={`${clientPath}/:id${customCaptionsPath}`}
						element={<ClientTerminology />}
					/>
				</Route>
				{/* APPLICATIONS PAGE */}
				<Route
					path={`${applicationListPath}/*`}
					element={<ApplicationPage />}
				/>
				{/* SITES PAGE */}
				<Route path={`${sitePath}/*`} element={<SitePage />} />
				{/* SITE APPLICATIONS PAGE */}

				<Route path={`${siteAppPath}/*`} element={<SiteAppPage />} />

				{/* MODEL PAGES */}
				<Route path={`${modelsPath}/*`} element={<WrappedModelsPage />} />
				<Route element={<AccessRoute access={access.modelAccess} />}>
					<Route path={`${modelImport}/:modelId`} element={<ModelMapData />} />
				</Route>
				{/* SERVICE PAGES */}
				<Route path={`${servicesPath}/*`} element={<WrappedServicesPage />} />
				{/* DEFECTS PAGES */}
				<Route path={`${defectsPath}/*`} element={<WrappedDefectsPage />} />
				{/*  ANALYTICS PAGES */}
				<Route path={`${newAnalyticsPath}/*`} element={<ExperimentalAnalytics />} />
				{/* FEEDBACK PAGES */}
				<Route path={`${feedbackPath}/*`} element={<WrappedFeedbackPage />} />
				{/* NOTICEBOARD PAGE	 */}
				<Route path={noticeboardPath} element={<NoticeBoardsPage />} />
				{/* RELEASE NOTES PAGE - intentionally available to any authenticated user;
					only the management screens below are SuperAdmin-gated. */}
				<Route path={releaseNotesPath} element={<ReleaseNotesPage />} />
				{/* RELEASE NOTES + MAINTENANCE BANNER ADMIN (SuperAdmin) */}
				<Route element={<RoleRoute roles={[roles.superAdmin]} />}>
					<Route path={releaseNotesAdminPath} element={<ReleaseNotesAdminPage />} />
					<Route path={maintenanceBannerAdminPath} element={<MaintenanceBannerAdminPage />} />
				</Route>
				{/* USER PAGES */}
				<Route path={usersPath} element={<UsersPage />} />
				{/* AI CONFIGURATION (SuperAdmin) */}
				<Route element={<RoleRoute roles={[roles.superAdmin]} />}>
					<Route
						path={`${aiConfigurationPath}/*`}
						element={<AIConfigurationPage />}
					/>
				</Route>
				{userRoutes.map((x) => {
					// Only the profile route (id 100 / userProfilePath) ever
					// receives a ?siteAppId=&subscriptionId= deep-link from
					// report emails, so it's the only one that needs the
					// tenant-switch HOC. The other user routes mount plain.
					const Element =
						x.id === 100 ? WrappedSingleComponent : SingleComponent;
					return (
						<Route
							key={x.id}
							path={x.path}
							element={<Element {...x} />}
						/>
					);
				})}
			</Route>
			<Route
				path="/report/:id/print"
				element={
					<ProtectedRoute>
						<Test />
					</ProtectedRoute>
				}
			/>
		</>
	)
);
function App() {
	return (
		<div className="App">
			<ErrorDialog />
			<SuccessDialog />
			<RouterProvider router={router} />
		</div>
	);
}

export default App;
